import { EntitiesApiUrl, get, post, put } from './ExternalApi';

/**
 * Phase 1: keep notification API paths centralized here so contract corrections stay isolated.
 * Existing env convention uses REACT_APP_VERCEL_API_* names; the legacy key is kept as fallback.
 */
export const NotificationApiUrl =
  process.env.REACT_APP_VERCEL_API_NotificationApiUrl ||
  process.env.REACT_APP_NOTIFICATION_API_URL ||
  EntitiesApiUrl;

export interface NotificationItem {
  id: string;
  websiteGuid: string | null;
  type: string;
  title: string;
  message: string;
  dataJson: string | null;
  severity: string | null;
  entityType: string | null;
  entityId: string | null;
  createdAtUtc: string;
  readAtUtc: string | null;
}

export interface NotificationPage {
  items: NotificationItem[];
  nextCursor: string | null;
}

export interface NotificationTypeOption {
  key: string;
  label: string;
}

export interface WebsiteNotificationPreferences {
  websiteGuid: string;
  enabledNotificationTypes: string[];
  supportedTypes: NotificationTypeOption[];
}

export interface NotificationListParams {
  websiteGuid: string;
  cursor?: string | null;
  limit?: number;
}

export type NotificationApiOperation =
  | 'list'
  | 'read'
  | 'read-all'
  | 'preferences-read'
  | 'preferences-save';

export class NotificationApiError extends Error {
  operation: NotificationApiOperation;
  status: number | null;
  code: string;
  cause: unknown;

  constructor(
    operation: NotificationApiOperation,
    message: string,
    options: { status?: number | null; code?: string; cause?: unknown } = {},
  ) {
    super(message);
    this.name = 'NotificationApiError';
    this.operation = operation;
    this.status = options.status ?? null;
    this.code = options.code || 'REQUEST_FAILED';
    this.cause = options.cause;
  }
}

type GenericObject = Record<string, unknown>;

function firstValue<T>(...values: Array<T | null | undefined>): T | null {
  return values.find(value => value !== undefined && value !== null) ?? null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function asOpaqueString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asObject(value: unknown): GenericObject | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as GenericObject;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value
    .filter((item): item is string => typeof item === 'string')
    .map(item => item.trim())
    .filter(Boolean)));
}

function unwrapPayload(raw: unknown): unknown {
  let payload = raw;
  for (let depth = 0; depth < 3; depth += 1) {
    const object = asObject(payload);
    if (!object) return payload;
    const wrapped = firstValue(object.result, object.Result, object.data, object.Data);
    if (wrapped === null) return payload;
    payload = wrapped;
  }
  return payload;
}

function requiredString(value: string | null, fieldName: string): string {
  if (!value) throw new Error(`NotificationApi: ${fieldName} is required`);
  return value;
}

function normalizeNotification(raw: any): NotificationItem | null {
  const source = asObject(raw);
  if (!source) return null;

  const id = asString(firstValue(source.id, source.Id, source.notificationId, source.NotificationId));
  const type = asString(firstValue(source.type, source.Type));
  const createdAtUtc = asString(firstValue(source.createdAtUtc, source.CreatedAtUtc, source.createdAt, source.CreatedAt));
  if (!id || !type || !createdAtUtc || Number.isNaN(Date.parse(createdAtUtc))) return null;

  return {
    id,
    websiteGuid: asNullableString(firstValue(source.websiteGuid, source.WebsiteGuid)),
    type,
    title: asString(firstValue(source.title, source.Title)) || '',
    message: asString(firstValue(source.message, source.Message)) || '',
    dataJson: asNullableString(firstValue(source.dataJson, source.DataJson)),
    severity: asNullableString(firstValue(source.severity, source.Severity)),
    entityType: asNullableString(firstValue(source.entityType, source.EntityType)),
    entityId: asNullableString(firstValue(source.entityId, source.EntityId)),
    createdAtUtc,
    readAtUtc: asNullableString(firstValue(source.readAtUtc, source.ReadAtUtc, source.readAt, source.ReadAt)),
  };
}

function normalizePage(raw: unknown): NotificationPage {
  const payload = unwrapPayload(raw);
  const sourceObject = asObject(payload);
  const source = Array.isArray(payload)
    ? payload
    : firstValue(sourceObject?.items, sourceObject?.Items, sourceObject?.notifications, sourceObject?.Notifications, []);
  const items = Array.isArray(source)
    ? source.map(normalizeNotification).filter((item): item is NotificationItem => item !== null)
    : [];
  return {
    items,
    nextCursor: asOpaqueString(firstValue(sourceObject?.nextCursor, sourceObject?.NextCursor)),
  };
}

function normalizePreferences(raw: unknown, websiteGuid: string): WebsiteNotificationPreferences {
  const payload = asObject(unwrapPayload(raw));
  const enabled = firstValue(payload?.enabledNotificationTypes, payload?.EnabledNotificationTypes, payload?.enabledTypes, payload?.EnabledTypes);
  const supported = firstValue(payload?.supportedTypes, payload?.SupportedTypes, payload?.notificationTypes, payload?.NotificationTypes);
  const supportedKeys = new Set<string>();

  return {
    websiteGuid: asString(firstValue(payload?.websiteGuid, payload?.WebsiteGuid)) || websiteGuid,
    enabledNotificationTypes: asStringArray(enabled),
    supportedTypes: Array.isArray(supported)
      ? supported
        .map((item: any) => {
          if (typeof item === 'string') {
            const key = asString(item);
            return key ? { key, label: key } : null;
          }

          const typedItem = asObject(item);
          const key = asString(firstValue(
            typedItem?.key,
            typedItem?.Key,
            typedItem?.type,
            typedItem?.Type,
            typedItem?.id,
            typedItem?.Id,
            typedItem?.value,
            typedItem?.Value,
          ));
          if (!key) return null;

          const label = asString(firstValue(
            typedItem?.label,
            typedItem?.Label,
            typedItem?.name,
            typedItem?.Name,
            typedItem?.title,
            typedItem?.Title,
            key,
          )) || key;
          return { key, label };
        })
        .filter((item): item is NotificationTypeOption => {
          if (!item) return false;
          if (supportedKeys.has(item.key)) return false;
          supportedKeys.add(item.key);
          return true;
        })
      : [],
  };
}

function getErrorStatus(error: any): number | null {
  return typeof error?.response?.status === 'number'
    ? error.response.status
    : typeof error?.status === 'number' ? error.status : null;
}

function getErrorCode(status: number | null): string {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'RATE_LIMITED';
  if (status !== null && status >= 400 && status < 500) return 'CLIENT_ERROR';
  return 'REQUEST_FAILED';
}

function getErrorMessage(error: any, operation: NotificationApiOperation): string {
  return error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || `Notification ${operation} request failed`;
}

async function request<T>(operation: NotificationApiOperation, callback: () => Promise<T>): Promise<T> {
  try {
    return await callback();
  } catch (error) {
    if (error instanceof NotificationApiError) throw error;
    const status = getErrorStatus(error);
    throw new NotificationApiError(operation, getErrorMessage(error, operation), {
      status,
      code: getErrorCode(status),
      cause: error,
    });
  }
}

export async function fetchNotifications(params: NotificationListParams): Promise<NotificationPage> {
  const websiteGuid = requiredString(asString(params?.websiteGuid), 'websiteGuid');
  const cursor = asOpaqueString(params?.cursor);
  const query = new URLSearchParams({
    websiteGuid,
    limit: String(Math.min(Math.max(params.limit ?? 25, 1), 100)),
  });
  if (cursor) query.set('cursor', cursor);

  return request('list', async () => {
    const raw = await get(`${NotificationApiUrl}/api/notifications?${query.toString()}`);
    return normalizePage(raw);
  });
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  const safeNotificationId = requiredString(asString(notificationId), 'notificationId');
  await request('read', () => post(`${NotificationApiUrl}/api/notifications/${encodeURIComponent(safeNotificationId)}/read`));
}

export async function markAllNotificationsRead(websiteGuid: string): Promise<void> {
  const safeWebsiteGuid = requiredString(asString(websiteGuid), 'websiteGuid');
  await request('read-all', () => post(`${NotificationApiUrl}/api/notifications/read-all`, { websiteGuid: safeWebsiteGuid }));
}

export async function fetchNotificationPreferences(websiteGuid: string): Promise<WebsiteNotificationPreferences> {
  const safeWebsiteGuid = requiredString(asString(websiteGuid), 'websiteGuid');
  return request('preferences-read', async () => {
    const raw = await get(`${NotificationApiUrl}/api/notification-preferences/${encodeURIComponent(safeWebsiteGuid)}`);
    return normalizePreferences(raw, safeWebsiteGuid);
  });
}

export async function saveNotificationPreferences(
  websiteGuid: string,
  enabledNotificationTypes: string[],
): Promise<void> {
  const safeWebsiteGuid = requiredString(asString(websiteGuid), 'websiteGuid');
  await request('preferences-save', () => put(`${NotificationApiUrl}/api/notification-preferences/${encodeURIComponent(safeWebsiteGuid)}`, {
    enabledNotificationTypes: asStringArray(enabledNotificationTypes),
  }));
}
